import React, { useState, useEffect } from 'react';
import type { RefObject } from 'react';
import { useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield';
import Form from '@atlaskit/form/Form';
import Field from '@atlaskit/form/Field';
import SearchIcon from '@atlaskit/icon/core/search';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import Spinner from '@atlaskit/spinner/spinner';

import { i18n } from '../AdvancedSearch-i18n.ts';
import { useSearchResultsContext } from '../SearchResultsProvider.tsx';
import { useSearchInputContext } from '../SearchInputProvider.tsx';
import { AskAIButton } from '../SmartAnswers/AskAIButton.tsx';

import {
	SearchBarContainer,
	TextFieldLabel,
	TextFieldContainer,
	SearchIconContainer,
	ClearIconContainer,
	SearchButtonContainer,
} from './PresentationalComponents.tsx';

interface SearchTextFieldProps {
	searchTextRef?: RefObject<HTMLInputElement>;
}

export const SearchTextField = ({ searchTextRef }: SearchTextFieldProps) => {
	const { onSearchQueryChange, onClickAskAI, searchQuery, aiSearchConfig } =
		useSearchInputContext();
	const [searchInput, setSearchInput] = useState('');
	const { loading } = useSearchResultsContext();

	useEffect(() => {
		setSearchInput(searchQuery);
	}, [searchQuery]);

	const intl = useIntl();

	const onSubmitSearchQuery = (formState) => {
		const updatedQuery = formState.searchQueryText.toString();
		onSearchQueryChange(updatedQuery);
	};

	const onChangeSearchQuery = (e) => {
		setSearchInput(e.currentTarget.value);
	};

	const onClearSearchQuery = () => {
		if (searchTextRef?.current) {
			searchTextRef.current.value = '';
		}
		if (searchInput) {
			setSearchInput('');
			onSearchQueryChange('');
		}
	};

	const onAskAI = () => {
		onClickAskAI(searchInput);
	};

	return (
		<Form onSubmit={onSubmitSearchQuery} testId="advanced-search-textfield">
			{({ formProps }) => (
				<form {...formProps} role="search">
					<TextFieldLabel htmlFor="advanced-search-textfield">
						{intl.formatMessage(i18n.textFieldLabel)}
					</TextFieldLabel>
					<SearchBarContainer>
						<TextFieldContainer>
							<Field name="searchQueryText" defaultValue={searchInput}>
								{({ fieldProps }) => (
									<Textfield
										{...fieldProps}
										aria-labelledby={undefined} // otherwise generates props pointing to nowhere
										aria-describedby={undefined} // otherwise generates props pointing to nowhere
										id="advanced-search-textfield"
										data-test-id="advanced-search-textfield"
										ref={searchTextRef}
										value={searchInput}
										onChange={onChangeSearchQuery}
										placeholder={intl.formatMessage(i18n.textFieldPlaceholder)}
										elemBeforeInput={
											<SearchIconContainer isSpinner={loading}>
												{loading ? (
													<Spinner size="small" testId="advanced-search-textloading" />
												) : (
													<SearchIcon
														label={intl.formatMessage(i18n.searchIconLabel)}
														color={token('color.icon')}
														spacing="spacious"
													/>
												)}
											</SearchIconContainer>
										}
										elemAfterInput={
											<SearchButtonContainer>
												{searchInput && (
													<ClearIconContainer onClick={onClearSearchQuery}>
														<CrossCircleIcon
															label={intl.formatMessage(i18n.crossIconLabel)}
															color={token('color.icon.subtle')}
														/>
													</ClearIconContainer>
												)}
												{aiSearchConfig ? (
													<AskAIButton onClick={onAskAI} isDisabled={!searchInput.length} />
												) : null}
											</SearchButtonContainer>
										}
										autoComplete="off"
									/>
								)}
							</Field>
						</TextFieldContainer>
					</SearchBarContainer>
				</form>
			)}
		</Form>
	);
};
